import { useEffect } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import {
  Gender_Enum,
  Order_State_Enum_Enum,
  useInsertOrderStateMutation,
  useUpdateOrderTestMutation,
} from '../generated/urql.administrator'
import { ClientIdentity, isIdentity } from '../lib/identity'
import toastsService from '../lib/toastsService'

export type UpdateOrderTestModalData = {
  isVisible: boolean
  id?: number
  tubeCode?: string | null
  orderId?: number
  currentOrderState?: Order_State_Enum_Enum
  firstName?: string | null
  lastName?: string | null
  identity?: unknown
  gender?: string | null
}

type UpdateOrderTestModalProps = {
  data: UpdateOrderTestModalData
  onClose: () => void
}

type FormValues = {
  tubeCode: string
  firstName: string
  lastName: string
  gender: '' | 'male' | 'female'
  identityType: '' | 'pesel' | 'id_card' | 'passport'
  identityValue: string
  identityCountryCode: string
  identityDateOfBirth: string
}

function identityToFormValues(identity: unknown): Pick<
  FormValues,
  'identityType' | 'identityValue' | 'identityCountryCode' | 'identityDateOfBirth'
> {
  if (!isIdentity(identity)) {
    return { identityType: '', identityValue: '', identityCountryCode: '', identityDateOfBirth: '' }
  }
  if (identity.type === 'pesel') {
    return { identityType: 'pesel', identityValue: identity.value, identityCountryCode: '', identityDateOfBirth: '' }
  }
  return {
    identityType: identity.type,
    identityValue: identity.value,
    identityCountryCode: identity.countryCode,
    identityDateOfBirth: identity.dateOfBirth,
  }
}

function formValuesToIdentity(
  identityType: FormValues['identityType'],
  identityValue: string,
  identityCountryCode: string,
  identityDateOfBirth: string,
): ClientIdentity | null {
  if (!identityType || !identityValue) return null
  if (identityType === 'pesel') return { type: 'pesel', value: identityValue }
  return { type: identityType, value: identityValue, countryCode: identityCountryCode, dateOfBirth: identityDateOfBirth }
}

const UpdateOrderTestModal = ({
  data: { isVisible, id, tubeCode, orderId, currentOrderState, firstName, lastName, identity, gender },
  onClose,
}: UpdateOrderTestModalProps) => {
  const [, updateOrderTest] = useUpdateOrderTestMutation()
  const [, insertOrderState] = useInsertOrderStateMutation()
  const {
    handleSubmit,
    reset,
    register,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>()

  const identityType = watch('identityType')

  useEffect(() => {
    if (isVisible) {
      reset({
        tubeCode: tubeCode ?? '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        gender: (gender as FormValues['gender']) ?? '',
        ...identityToFormValues(identity),
      })
    }
  }, [isVisible, tubeCode, firstName, lastName, gender, identity, reset])

  const onSubmit = async ({
    tubeCode,
    firstName,
    lastName,
    gender,
    identityType,
    identityValue,
    identityCountryCode,
    identityDateOfBirth,
  }: FormValues) => {
    if (!id) return

    const { error } = await updateOrderTest({
      id,
      tubeCode,
      retrievedOn: new Date(),
      firstName,
      lastName,
      gender: (gender as Gender_Enum) || null,
      identity: formValuesToIdentity(identityType, identityValue, identityCountryCode, identityDateOfBirth),
    })

    if (error) {
      toastsService.add({ title: 'Błąd!', message: error.message, variant: 'danger' })
      return
    }

    if (orderId && currentOrderState !== Order_State_Enum_Enum.ExaminingSamples) {
      const { error: stateError } = await insertOrderState({
        orderId,
        state: Order_State_Enum_Enum.ExaminingSamples,
      })
      if (stateError) {
        toastsService.add({ title: 'Błąd!', message: stateError.message, variant: 'danger' })
        return
      }
    }

    toastsService.add({ title: 'Sukces!', message: 'Pomyślnie zapisano zmiany', variant: 'success' })
    onClose()
  }

  return (
    <Modal
      show={isVisible}
      onHide={onClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Edycja</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="tubeCodeInput">
            <Form.Label>Kod probówki</Form.Label>
            <Form.Control autoFocus {...register('tubeCode')} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="firstNameInput">
            <Form.Label>Imię</Form.Label>
            <Form.Control {...register('firstName')} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="lastNameInput">
            <Form.Label>Nazwisko</Form.Label>
            <Form.Control {...register('lastName')} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="genderInput">
            <Form.Label>Płeć</Form.Label>
            <Form.Select {...register('gender')}>
              <option value="">— brak —</option>
              <option value={Gender_Enum.Female}>Kobieta</option>
              <option value={Gender_Enum.Male}>Mężczyzna</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="identityTypeInput">
            <Form.Label>Typ tożsamości</Form.Label>
            <Form.Select {...register('identityType')}>
              <option value="">— brak —</option>
              <option value="pesel">PESEL</option>
              <option value="id_card">Dowód osobisty</option>
              <option value="passport">Paszport</option>
            </Form.Select>
          </Form.Group>

          {identityType !== '' && (
            <Form.Group className="mb-3" controlId="identityValueInput">
              <Form.Label>
                {identityType === 'pesel' ? 'PESEL' : identityType === 'id_card' ? 'Numer dowodu' : 'Numer paszportu'}
              </Form.Label>
              <Form.Control {...register('identityValue')} />
            </Form.Group>
          )}

          {(identityType === 'id_card' || identityType === 'passport') && (
            <>
              <Form.Group className="mb-3" controlId="identityCountryCodeInput">
                <Form.Label>Kod kraju</Form.Label>
                <Form.Control {...register('identityCountryCode')} />
              </Form.Group>
              <Form.Group className="mb-3" controlId="identityDateOfBirthInput">
                <Form.Label>Data urodzenia</Form.Label>
                <Form.Control type="date" {...register('identityDateOfBirth')} />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Anuluj
          </Button>
          <Button
            style={{ width: 75 }}
            type="submit"
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex justify-center w-24">
                <Spinner size="sm" animation="border" role="status" />
              </div>
            ) : (
              'Zapisz'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default UpdateOrderTestModal
